import React, { FC, useCallback, useEffect, useMemo, useState } from "react"
import { observer } from "mobx-react-lite"
import { Appearance, ViewStyle } from "react-native"
import { Button, FlatList, ListRenderItem, Screen, Text } from "../../../components"
import { CompositeScreenProps } from "@react-navigation/native"
import { color } from "../../../theme"
import { SafeAreaView } from "react-native-safe-area-context"
import { translate, TxKeyPath } from "../../../i18n"
import { Checkbox, View } from "native-base"
import { goBack } from "../../../navigators"
import { useStores } from "../../../models"
import _ from "lodash"

const ROOT: ViewStyle = {
  flex: 1,
}

export const chemicalParameter = [
  { id: 11, code: 'ammonia', get name() {return `${_.startCase(translate('multiMeasurements.ammonia'))} (${translate('multiMeasurements.ammonia_short')})`}, unit: 'ppm' },
  { id: 12, code: 'nitrat', get name() {return `${_.startCase(translate('multiMeasurements.nitrat'))} (${translate('multiMeasurements.nitrat_short')})`}, unit: 'ppm' },
  { id: 14, code: 'alkalinity', get name() {return `${_.startCase(translate('multiMeasurements.alkalinity'))} (${translate('multiMeasurements.alkalinity_short')})`}, unit: 'ppm' },
  { id: 13, code: 'nitrit', get name() {return `${_.startCase(translate('multiMeasurements.nitrit'))} (${translate('multiMeasurements.nitrit_short')})`}, unit: 'ppm' },
  { id: 15, code: 'hardness', get name() {return `${_.startCase(translate('multiMeasurements.hardness'))} (${translate('multiMeasurements.hardness_short')})`}, unit: 'ppm' },
  { id: 16, code: 'calcium', get name() {return `${_.startCase(translate('multiMeasurements.calcium'))} (${translate('multiMeasurements.calcium_short')})`}, unit: 'ppm' },
  { id: 17, code: 'magnesium', get name() {return `${_.startCase(translate('multiMeasurements.magnesium'))} (${translate('multiMeasurements.magnesium_short')})`}, unit: 'ppm' },
  { id: 18, code: 'carbonat', get name() {return `${_.startCase(translate('multiMeasurements.carbonat'))} (${translate('multiMeasurements.carbonat_short')})`}, unit: 'ppm' },
  { id: 19, code: 'bicarbonat', get name() {return `${_.startCase(translate('multiMeasurements.bicarbonat'))} (${translate('multiMeasurements.bicarbonat_short')})`}, unit: 'ppm' },
  { id: 20, code: 'tom', get name() {return `${_.startCase(translate('multiMeasurements.tom'))} (${translate('multiMeasurements.tom_short')})`}, unit: 'ppm' },
  { id: 24, code: 'orp', get name() {return `${_.startCase(translate('multiMeasurements.orp'))} (${translate('multiMeasurements.orp_short')})`}, unit: 'CFU/ml' },
  { id: 35, code: 'ammonium', get name() {return `${_.startCase(translate('multiMeasurements.ammonium'))} (${translate('multiMeasurements.ammonium_short')})`}, unit: 'ppm' },
  { id: 36, code: 'phosphate', get name() {return `${_.startCase(translate('multiMeasurements.phosphate'))} (${translate('multiMeasurements.phosphate_short')})`}, unit: 'ppm' }
];

export const planktonParameter = [
  { id: 25, code: 'ga', shortName: 'GA', get name() {return `${_.startCase(translate('multiMeasurements.ga'))} (${translate('multiMeasurements.ga_short')})`}, unit: 'cell/ml' },
  { id: 26, code: 'bga', shortName: 'BGA', get name() {return `${_.startCase(translate('multiMeasurements.bga'))} (${translate('multiMeasurements.bga_short')})`}, unit: 'cell/ml' },
  { id: 21, code: 'total_plankton', shortName: 'Total', get name() {return `${_.startCase(translate('multiMeasurements.total_plankton'))} (${translate('multiMeasurements.total_plankton_short')})`}, unit: 'cell/ml' },
  { id: 27, code: 'dino', shortName: 'DINO', get name() {return `${_.startCase(translate('multiMeasurements.dino'))} (${translate('multiMeasurements.dino_short')})`}, unit: 'cell/ml' },
  { id: 28, code: 'diatom', shortName: 'DIATOM', get name() {return `${_.startCase(translate('multiMeasurements.diatom'))} (${translate('multiMeasurements.diatom_short')})`}, unit: 'cell/ml' },
  { id: 29, code: 'prozoo', shortName: 'PRO/ZOO', get name() {return `${_.startCase(translate('multiMeasurements.prozoo'))} (${translate('multiMeasurements.prozoo_short')})`}, unit: 'cell/ml' },
  { id: 30, code: 'floc', shortName: 'FLOC', get name() {return `${_.startCase(translate('multiMeasurements.floc'))} (${translate('multiMeasurements.floc_short')})`}, unit: 'ml' },
  { id: 37, code: 'gba', shortName: 'GBA', get name() {return `${_.startCase(translate('multiMeasurements.gba'))} (${translate('multiMeasurements.gba_short')})`}, unit: 'cell/ml' },
  { id: 38, code: 'euglenophyta', shortName: 'Euglenid', get name() {return `${_.startCase(translate('multiMeasurements.euglenophyta'))} (${translate('multiMeasurements.euglenophyta_short')})`}, unit: 'cell/ml' },
  { id: 51, code: 'gga', shortName: 'GGA', get name() {return `${_.startCase(translate('multiMeasurements.gga'))} (${translate('multiMeasurements.gga_short')})`}, unit: 'cell/ml' },
  { id: 52, code: 'yga', shortName: 'YGA', get name() {return `${_.startCase(translate('multiMeasurements.yga'))} (${translate('multiMeasurements.yga_short')})`}, unit: 'cell/ml' },
  { id: 53, code: 'haptyophyta', shortName: 'Haptophyta', get name() {return `${_.startCase(translate('multiMeasurements.haptyophyta'))} (${translate('multiMeasurements.haptyophyta_short')})`}, unit: 'cell/ml' }
];

export const microbioParameter = [
  { id: 23, code: 'total_vibrio', shortName: 'Total Vibrio', get name() {return `${_.startCase(translate('multiMeasurements.total_vibrio'))} (${translate('multiMeasurements.total_vibrio_short')})`}, unit: 'CFU/ml' },
  { id: 31, code: 'yellow', shortName: 'Yellow', get name() {return `${_.startCase(translate('multiMeasurements.yellow'))} (${translate('multiMeasurements.yellow_short')})`}, unit: 'CFU/ml' },
  { id: 32, code: 'green', shortName: 'Green', get name() {return `${_.startCase(translate('multiMeasurements.green'))} (${translate('multiMeasurements.green_short')})`}, unit: 'CFU/ml' },
  { id: 33, code: 'black', shortName: 'Black', get name() {return `${_.startCase(translate('multiMeasurements.black'))} (${translate('multiMeasurements.black_short')})`}, unit: 'CFU/ml' },
  { id: 34, code: 'luminance', shortName: 'Luminance', get name() {return `${_.startCase(translate('multiMeasurements.luminance'))} (${translate('multiMeasurements.luminance_short')})`}, unit: 'CFU/ml' }
];

export const ParameterListScreen: FC<{ colorScheme?: "light" | "dark" } & CompositeScreenProps<any, any>> = observer(function ParameterListScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), route: { params: { route = null } } } = props;

  const [ selectedParameter, setSelectedParameter ] = useState<Array<any>>([]);

  // Pull in one of our MST stores
  const { appStore: { farmStore } } = useStores()

  useEffect(() => {
    let initial = [];

    if (route && route === "plankton") initial = farmStore.planktonParameter.length === 0 ? planktonParameter.slice(0, 3) : [...farmStore.planktonParameter];
    if (route && route === "microbio") initial = farmStore.microbioParameter.length === 0 ? microbioParameter.slice(0, 3) : [...farmStore.microbioParameter];
    if (route && route === "chemical") initial = farmStore.parameter.length === 0 ? chemicalParameter.slice(0, 3) : [...farmStore.parameter];
    setSelectedParameter(initial);
  }, [farmStore, route])

  const parameters = useMemo<Array<any>>(() => {
    return selectedParameter
  }, [selectedParameter])

  const initialParameter = useMemo<Array<any>>(() => {
    let initialParameter = [];
    if (route && route === "plankton") initialParameter = planktonParameter;
    if (route && route === "microbio") initialParameter = microbioParameter;
    if (route && route === "chemical") initialParameter = chemicalParameter;
    return initialParameter
  }, [route])

  const handleSelect = useCallback((item) => {
    const isExist = parameters.find(param => param.id === item.id);
    const isFull = parameters.length === 3;
    if (isExist) {
      const removeIndex = parameters.findIndex(param => param.id === item.id)
      parameters.splice(removeIndex, 1);
      setSelectedParameter([...parameters]);
    } else if (!isFull) {
      parameters.push(item);
      setSelectedParameter([...parameters]);
    }
  }, [parameters])

  const handleSave = () => {
    if (route && route === "plankton") farmStore.set('planktonParameter', parameters);
    if (route && route === "microbio") farmStore.set('microbioParameter', parameters);
    if (route && route === "chemical") farmStore.set('parameter', parameters);
    goBack('Home.Stack');
  }

  const keyExtractor = useCallback((item) => `${item.id}`, [])

  const renderItem: ListRenderItem<any> = useCallback(({ item }) => {
    const isChecked = parameters.find(param => param.id === item.id);
    const isDisabled = parameters.length === 3 && !isChecked;
    return (
      <View p="0px" ml="16px">
        <Checkbox
          value={item}
          colorScheme="blue"
          borderColor={color.palette.dust500}
          onChange={() => handleSelect(item)}
          isChecked={isChecked}
          isDisabled={isDisabled}
        >
          <Text text={`${item.name} (${item.unit})`} preset="body1" py="10px" mx="6px" color={color.palette.darkGrey} colorScheme={colorScheme} isTruncated />
        </Checkbox>
      </View>
    )
  }, [colorScheme, handleSelect, parameters])

  const ParameterHeader = useCallback(() => {
    return (
      <View p="16px">
        <View flexDirection="row" justifyContent="space-between" mb="10px">
          <Text preset="title2" tx={`parameterListScreen.${route}` as TxKeyPath} color={color.primaryDarker} colorScheme={colorScheme} />
          <Text preset="title2" text={`${selectedParameter.length}/3`} color={color.primaryDarker} colorScheme={colorScheme} />
        </View>
        <Text preset="body2" tx="parameterListScreen.info" colorScheme={colorScheme} />
      </View>
    )
  }, [colorScheme, route, selectedParameter.length])


  return (
    <Screen
      style={ROOT}
      preset="fixed"
      colorScheme={colorScheme}
      backgroundBar={colorScheme === "light" ? color.primary : undefined}
      statusBar={colorScheme === 'light' ? 'light-content' : 'dark-content'}
    >
      <ParameterHeader />
       <FlatList
        key={parameters.join(",")}
        data={initialParameter}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        keyboardShouldPersistTaps="handled"
        estimatedItemSize={40}
        ListFooterComponent={<SafeAreaView edges={['bottom']}/>}
        showsVerticalScrollIndicator={false}
      />
      <View p="16px">
        <Button
          testID="jala:id/save-button"
          tx="save" onPress={handleSave} colorScheme={colorScheme}
        />
      </View>
    </Screen>
  )
})
